<template>
    <div>
        <!--
            Invoice delivery, restored 2026-08-09 after Stage 5 hid it.

            This is not an automation setting, which is why it is back. It is the
            only thing that decides where an invoice is emailed:
            Customer::resolveInvoiceRecipientEmail() sends a portal customer's
            invoice to the internal address below and nowhere else, and everyone
            else's to their own invoice address. Hiding it left that unsettable,
            and left every new customer on whatever the column defaults to.

            Two options, not three. `auto_internal` and `manual_internal` both
            mean "email the customer" now that nothing generates automatically,
            so the stored distinction between them no longer describes anything;
            a customer still on `auto_internal` reads and behaves as Standard,
            and saving the form settles them on `manual_internal`.
        -->
        <div class="form-group my-3 mt-4">
            <label><strong>Invoice Delivery</strong></label>
            <select v-model="generationMode" class="form-select" name="invoice_generation_mode">
                <option value="manual_internal">Standard &mdash; we email the invoice to the customer</option>
                <option value="external_portal">Billing portal &mdash; a member of staff enters it into the customer's portal</option>
            </select>
            <div v-for="(error, index) in fieldErrors('invoice_generation_mode')" :key="`invoice_generation_mode-${index}`" class="text-danger small mt-1">
                {{ error }}
            </div>
        </div>

        <div v-if="generationMode === 'external_portal'" class="form-group mb-4">
            <label for="portal_invoice_internal_email"><strong>Portal Invoice Internal Recipient</strong></label>
            <input
                v-model="portalInvoiceInternalEmail"
                class="form-control"
                type="email"
                maxlength="255"
                id="portal_invoice_internal_email"
                name="portal_invoice_internal_email"
            >
            <small class="form-text text-muted d-block pt-0.5 ps-1">
                <i class="fa fa-info-circle me-1" aria-hidden="true"></i>
                Invoices for this customer are emailed <strong>here</strong>, for a member of staff to
                enter into the customer's billing portal. <strong>The customer receives no email.</strong>
                There is no fallback, so leaving this empty means the invoice reaches nobody.
            </small>
            <div v-for="(error, index) in fieldErrors('portal_invoice_internal_email')" :key="`portal_invoice_internal_email-${index}`" class="text-danger small mt-1">
                {{ error }}
            </div>
        </div>

        <!--
            Stage 5 — invoice automation retired (2026-08-09).

            Everything between here and the bank-details checkboxes below
            configured a pipeline that no longer runs. The columns stay; only the
            inputs are hidden.

            The generation mode is *not* among them — it is above, as Invoice
            Delivery, because it is live routing rather than automation. That
            distinction was missed when this was first commented out.

            The summary banner went with them rather than being left in place.
            It announced a schedule — "Invoice generation is scheduled daily at
            12:00am" — which after this change is simply untrue, and a panel
            stating a false schedule is worse than one saying nothing.

            <div class="alert alert-info text-center py-3 px-3 mb-3" role="status">
                <div class="fw-semibold">{{ groupingSummary }}</div>
                <div class="small mt-1">{{ schedulingSummary }}</div>
            </div>

            <div class="form-group mb-4">
                <label><strong>Invoice Grouping Rule</strong></label>
                <select v-model="groupingBasis" class="form-select" name="invoice_grouping_basis">
                    <option v-for="basis in groupingOptions" :key="basis.value" :value="basis.value">
                        {{ basis.label }}
                    </option>
                </select>
                <div v-for="(error, index) in fieldErrors('invoice_grouping_basis')" :key="`invoice_grouping_basis-${index}`" class="text-danger small mt-1">
                    {{ error }}
                </div>
            </div>

            <div v-if="generationMode === 'auto_internal'">
                <div class="form-group mb-4">
                    <label><strong>Invoice Processing Frequency</strong></label>
                    <select v-model="processingFrequency" class="form-select" name="invoice_processing_frequency">
                        <option v-for="frequency in processingFrequencies" :key="frequency.value" :value="frequency.value">
                            {{ frequency.label }}
                        </option>
                    </select>
                </div>

                <div v-if="processingFrequency === 'inactivity_window'" class="form-group mb-4">
                    <label><strong>Invoice Inactivity Window (hours)</strong></label>
                    <input v-model.number="inactivityHours" class="form-control" type="number" min="1" max="8760" name="invoice_inactivity_hours">
                </div>

                <div v-if="processingFrequency !== 'inactivity_window'" class="form-group mb-4">
                    <label><strong>Scheduled Time (HH:MM)</strong></label>
                    <input v-model="scheduledTime" class="form-control" type="time" name="scheduled_time">
                </div>

                <div v-if="processingFrequency !== 'inactivity_window'" class="form-group mb-4">
                    <label><strong>Invoice Processing Delay Window (minutes)</strong></label>
                    <input v-model.number="processingDelayMinutes" class="form-control" type="number" min="0" max="10080" name="invoice_processing_time">
                </div>

                <div v-if="processingFrequency === 'weekly'" class="form-group mb-4">
                    <label><strong>Weekly Scheduled Day</strong></label>
                    <select v-model.number="processingWeekday" class="form-select" name="invoice_processing_weekday">
                        <option v-for="day in weekdays" :key="day.value" :value="day.value">
                            {{ day.label }}
                        </option>
                    </select>
                </div>
            </div>
        -->

        <!--
            Kept, and the reason the component is not hidden wholesale: all three
            of these predate this branch. Main rendered them as inline checkboxes
            on the customer create/edit blades and the branch moved them in here,
            so hiding the panel would have silently dropped three working fields.
        -->
        <div class="form-check mb-2">
            <input class="form-check-input" type="checkbox" name="force_alt_terms" id="force_alt_terms" :checked="forceAltTerms">
            <label class="form-check-label" for="force_alt_terms"><strong>Always Display Newer Bank Details</strong></label>
        </div>

        <div class="form-check mb-2">
            <input class="form-check-input" type="checkbox" name="force_old_terms" id="force_old_terms" :checked="forceOldTerms">
            <label class="form-check-label" for="force_old_terms"><strong>Always Display Original Bank Details</strong></label>
        </div>

        <div class="form-check mb-2">
            <input v-model="requiresPoForInvoice" class="form-check-input" name="requires_po_for_invoice" type="checkbox" id="requires_po_for_invoice">
            <label class="form-check-label" for="requires_po_for_invoice"><strong>Requires PO for Invoice</strong></label>
        </div>

        <!--
            Stage 5 — hidden with the rest, though it was not on the plan's list.

            It is only read by the work item processing service and by the
            retired queue screen's filter; the manual paths email an invoice as
            they raise it, whatever this says. Left visible it would keep
            promising "Invoices will be emailed to X after generation" to
            auto_internal customers — a promise nothing keeps once nothing
            generates automatically.

            <div v-if="generationMode === 'auto_internal'" class="form-check mb-2">
                <input v-model="autoEmailInvoices" class="form-check-input" name="auto_email_invoices" type="checkbox" id="auto_email_invoices">
                <label class="form-check-label" for="auto_email_invoices"><strong>Auto Email Invoices</strong></label>
                <small class="form-text text-muted d-block pt-0.5 ps-1">
                    <i class="fa fa-info-circle me-1" aria-hidden="true"></i>
                    Invoices will be emailed to <strong>{{ autoEmailTargetEmail }}</strong> after generation.
                </small>
            </div>
        -->
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
    initial: {
        type: Object,
        required: true,
    },
    errors: {
        type: Object,
        required: false,
        default: () => ({}),
    },
});

const groupingOptions = [
    { value: 'jk_reference', label: 'Per JK reference' },
    { value: 'job_type', label: 'Per matching job type (ignore date)' },
    { value: 'job_type_and_date', label: 'Per matching job type and date' },
];

const processingFrequencies = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekdays', label: 'Daily (week days only)' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'fortnightly', label: 'Fortnightly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'inactivity_window', label: 'After inactivity window' },
];

const weekdays = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
];

// Anything that is not the portal is standard delivery, so a customer still
// carrying the retired `auto_internal` lands on Standard rather than on an
// option that no longer exists.
const generationMode = ref(
    props.initial.invoice_generation_mode === 'external_portal' ? 'external_portal' : 'manual_internal'
);
const portalInvoiceInternalEmail = ref(props.initial.portal_invoice_internal_email ?? '');
const groupingBasis = ref(props.initial.invoice_grouping_basis ?? 'jk_reference');
const processingFrequency = ref(props.initial.invoice_processing_frequency ?? 'daily');
const processingDelayMinutes = ref(props.initial.invoice_processing_time ?? 0);
const inactivityHours = ref(Number(props.initial.invoice_inactivity_hours ?? 48));
const scheduledTime = ref(props.initial.scheduled_time ?? '00:00');
const processingWeekday = ref(Number(props.initial.invoice_processing_weekday ?? 1));
const requiresPoForInvoice = ref(Boolean(props.initial.requires_po_for_invoice ?? false));
const autoEmailInvoices = ref(Boolean(props.initial.auto_email_invoices ?? false));
const invoiceRecipientEmail = ref(props.initial.invoice_recipient_email ?? '');
const accountContactEmail = ref(props.initial.account_contact_email ?? '');
const forceAltTerms = ref(Boolean(props.initial.force_alt_terms ?? false));
const forceOldTerms = ref(Boolean(props.initial.force_old_terms ?? false));

function fieldErrors(field) {
    return props.errors[field] ?? [];
}

function formatTime12Hour(timeValue) {
    const time = typeof timeValue === 'string' && timeValue.length >= 5 ? timeValue : '00:00';
    const [hourPart, minutePart] = time.split(':');
    const hour24 = Number(hourPart);
    const minute = String(minutePart ?? '00').padStart(2, '0');

    if (!Number.isFinite(hour24)) {
        return '12:00am';
    }

    const period = hour24 >= 12 ? 'pm' : 'am';
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;

    return `${hour12}:${minute}${period}`;
}

const groupingSummary = computed(() => {
    const poPrefix = requiresPoForInvoice.value ? 'PO required.' : 'No PO required.';
    const groupingSummaryByBasis = {
        jk_reference: 'Invoices are grouped by JK reference.',
        job_type: 'Invoices are grouped by matching job type, date ignored.',
        job_type_and_date: 'Invoices are grouped by matching job type and date.',
    };

    return `${poPrefix} ${groupingSummaryByBasis[groupingBasis.value] ?? groupingSummaryByBasis.jk_reference}`;
});

const schedulingSummary = computed(() => {
    if (generationMode.value === 'manual_internal') {
        return 'Invoice generation is scheduled for manual internal processing.';
    }

    if (generationMode.value === 'external_portal') {
        return 'Invoice generation is scheduled through the external portal workflow.';
    }

    const delayMinutes = Number(processingDelayMinutes.value ?? 0);
    const delaySuffix = delayMinutes > 0 ? ` with a ${delayMinutes}-minute processing delay window.` : '.';

    if (processingFrequency.value === 'inactivity_window') {
        const inactivityWindowHours = Number(inactivityHours.value ?? 48);
        return `Invoice generation is scheduled for ${inactivityWindowHours} hours after the last work item activity.`;
    }

    const scheduledAt = formatTime12Hour(scheduledTime.value);

    if (processingFrequency.value === 'weekly') {
        const weekday = weekdays.find((day) => day.value === Number(processingWeekday.value ?? 1))?.label ?? 'Monday';
        return `Invoice generation is scheduled for every ${weekday} at ${scheduledAt}${delaySuffix}`;
    }

    if (processingFrequency.value === 'fortnightly') {
        return `Invoice generation is scheduled fortnightly at ${scheduledAt}${delaySuffix}`;
    }

    if (processingFrequency.value === 'monthly') {
        return `Invoice generation is scheduled monthly at ${scheduledAt}${delaySuffix}`;
    }

    return `Invoice generation is scheduled daily at ${scheduledAt}${delaySuffix}`;
});

const autoEmailTargetEmail = computed(() => {
    const rawEmail = `${invoiceRecipientEmail.value || accountContactEmail.value || ''}`.trim();
    return rawEmail.length > 0 ? rawEmail : 'the configured invoice recipient email';
});
</script>
